/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { useCallback, useEffect, useRef, useState } from 'react'
import dayjs from 'dayjs'
import colors from '@sportninja/common/constants/appColors'
import { PRIMARY_BUTTON_CONTENT } from '@sportninja/common/constants/buttonColors'
import { connect } from 'react-redux'
import { FloatButton } from 'antd'
import { ArrowUpOutlined, CalendarOutlined } from '@ant-design/icons'
import {
  getAllOfficialTypes,
  getGameStatusTypesBySportId,
  getGameTypesBySportId,
} from '@sportninja/common/selectors/types'
import req from '@sportninja/common/api/request'
import { resolvePastWindowStart } from '@sportninja/common/features/event/resolvePastWindowStart'
import { findScheduleStartsAt } from '@sportninja/common/features/competition/utils/findScheduleStartsAt'
import { calendarAnchorForRange } from '@sportninja/common/features/game/gameListView/calendarAnchor'
import { todayCivilDate } from '@sportninja/common/features/game/gameListView/venueLocalDateTime'
import { groupGamesByDay } from '@sportninja/common/features/game/gameListView/gameDayBucket'
import EventListRow from 'src/features/event/components/EventListRow'
import { useScheduleEventStream } from '@sportninja/common/features/event/hooks/useScheduleEventStream'
import { useCompetitionEventStream } from '@sportninja/common/features/event/hooks/useCompetitionEventStream'
import { useScheduleStream } from '@sportninja/common/features/event/hooks/useScheduleStream'
import { frontierOf } from '@sportninja/common/features/event/scheduleWatermark'
import { useEventExistsFromToday } from '@sportninja/common/features/event/hooks/useEventExistsFromToday'
import { resolveDecisionEventQuery } from '@sportninja/common/features/event/resolveDecisionEventQuery'
import {
  listRosterEventsPage,
  listScheduleEventsPage,
  listTeamEventsPage,
} from '@sportninja/common/services/event'
import { queryKeys } from '@sportninja/common/config/queryKeys'
import { buildCompetitionEventsQuery } from '@sportninja/common/features/event/buildCompetitionEventsQuery'
import { resolveRosterIdFromFilters } from '@sportninja/common/features/event/resolveRosterIdFromFilters'
import { filterEvents } from '@sportninja/common/features/event/filterEvents'
import { mergeEventsIntoDayBuckets } from '@sportninja/common/features/event/mergeEventsIntoDayBuckets'
import { resolveDefaultScheduleTab } from '@sportninja/common/features/event/resolveDefaultScheduleTab'
import { shouldRedecideScheduleTab } from '@sportninja/common/features/event/shouldRedecideScheduleTab'
import { resolveTabForDate } from '@sportninja/common/features/event/resolveTabForDate'

import { media, Mobile } from '../../Responsive'
import ListStatusView from '../StatusView'
import EmptyState from 'src/components/ts-components/EmptyState'
import ScheduleCardSkeleton from 'src/components/ts-components/ScheduleCardSkeleton'
import useGetScheduleFiltering from '../useGetScheduleFiltering'
import GameListFilters from './GameListFilters'
import TimePeriodFilter from 'src/pages/MySchedule/components/TimePeriodFilter'
import { useUrlManager } from 'src/hooks/useUrlManager'

import {
  CalendarPanel,
  DayOfGames,
  DetailsPanel,
  LoadMoreFooter,
  ViewSwitcher,
  useGameListItems,
  useCalendarAnchor,
  useGameViewMode,
  useVenueOptions,
  buildGameListQueries,
  calendarViewportRange,
  resolveGameListWindow,
  gameViewsLabel,
} from 'src/features/game/gameListView'
import { formatCivilDay } from './CalendarView/buildCalendarDays'

const SCROLL_THRESHOLD = 500

// Re-exported so `MyScheduleList` and `OrgScheduleList` keep importing it from
// here. Pointing them at the feature folder instead would make two unrelated
// pages depend on the Schedule views for a plain presentational component.
export { DayOfGames } from 'src/features/game/gameListView'

const toCivilRange = (startsAt, endsAt) => {
  const start = startsAt ? dayjs(startsAt) : null
  const end = endsAt ? dayjs(endsAt) : null
  const hasStart = start !== null && start.isValid()
  const hasEnd = end !== null && end.isValid()

  // Only a range with NEITHER bound is "no range". The picker lets an admin set
  // just one end, and collapsing that to null silently widens the query to
  // everything.
  if (!hasStart && !hasEnd) {
    return null
  }

  return {
    start: hasStart ? start.format('YYYY-MM-DD') : null,
    // The picker's end is the last day the user wants INCLUDED.
    endExclusive: hasEnd ? end.add(1, 'day').format('YYYY-MM-DD') : null,
  }
}

const GameList = ({
  team_id,
  schedule_id,
  gameStatusTypes,
  gameTypes,
  officialTypes,
  /*
   * The organization the officials pool is scoped to. Only the Schedule host
   * knows it; without it the officials columns render read-only, because the
   * search endpoint returns every official on the platform when it is not
   * given both an organization AND a competition.
   *
   * The Details grid's edit modal uses the same id to enable its "create a new
   * location" row, and to label its "Within <org>" scope toggle.
   */
  organizationId = null,
  slug: _slug = '/games',
  refreshKey,
  setVisibleGameIds,
  areFiltersHidden,
  /*
   * Set by a host that already shows the switcher somewhere better — the
   * Schedule page puts it on the Live / Scheduler Admin row. Hosts without such
   * a row (My Schedule, Team, Org) keep the one in the filter row, so this is a
   * placement choice, not a feature flag.
   */
  hasHostViewSwitcher = false,
  setFilterForExporting = () => {},
  isSoccer = false,
  isHockey = false,
  onClickFirstGame = () => {},
  // this is only for team filtering
  scheduleId = null,
  // The selected competition's `starts_at`. PAST reaches back to it rather
  // than the flat 2-year floor — a schedule holds nothing earlier than it
  // began. Absent (a team page, say) falls back to the floor.
  scheduleStartsAt = null,
  /**
   * Reports the team roster resolved for the current season/division, so the
   * Team page can enable its Add Event action without waiting for the Roster
   * tab to mount. Called with '' when nothing resolves (All Seasons, or a team
   * not in the selected competition).
   */
  onRosterIdChange,
  /**
   * Receives a `(event) => void` callback that jumps the list to whichever
   * period holds that event. The Team page calls it after a create/update so
   * the list follows the event the user just saved — creating a next-week
   * event while reading Past would otherwise leave them on a tab that does
   * not show it.
   */
  onFollowEventReady,
  // Details editing is gated on the schedule's update permission. Defaults to
  // read-only so a consumer that does not pass it cannot accidentally hand
  // editing to someone who only has scoring rights.
  canUpdate = false,
  // Sport whose game types the edit modal offers. Already used by
  // `mapStateToProps` below; destructured here so it can be passed on.
  sportId = null,
  // Labels the edit modal's "Within <org>" location-scope toggle.
  organizationName = null,
  // Whether the Details grid's edit modal offers Delete. Defaults to false so
  // a consumer that does not pass it cannot hand out deletion by omission.
  canDeleteGames = false,
  /*
   * Whether this host offers the Details view at all. Defaults to true so the
   * competition-level hosts (Schedule, Org Schedule) are unaffected; the Team
   * page turns it off, where the list is one team's games across competitions
   * rather than a single competition's schedule.
   *
   * Passed into `useGameViewMode` as well as the switcher, so a `sn_view=details`
   * deep link cannot open a view the page has no control to leave.
   */
  showDetailsView = true,
}) => {
  const {
    currentParams,
    teams,
    team,
    setTeam,
    filters,
    dateRange,
    setDateRange,
    state,
    setState,
    sort,
    setSort,
    loaded,
    venues,
    venue,
    setVenue,
    facility,
    setFacility,
    loadingVenues,
  } = useGetScheduleFiltering(
    {
      team_id,
      schedule_id,
    },
    {
      autoSelectMostRecentComp: true,
      onScheduleChange: (id) => {
        setSelectedScheduleId(id)
        setDatesResolved(false)
        setSlug(`/schedules/${id}/games`)
      },
    }
  )

  const [selectedScheduleId, setSelectedScheduleId] = useState(
    currentParams?.sn_division ||
      currentParams?.sn_schedule ||
      schedule_id ||
      ''
  )
  /**
   * The selected schedule's `starts_at`, for the PAST window's lower bound.
   *
   * Looked up in `filters` — the same `[{ name, schedules }]` shape from BOTH
   * `/teams/:id/schedules/V2` (team page) and `/schedules/:id/children/dropdown`
   * (competition page) — so the bound follows whatever season or division the
   * user actually has selected. The `scheduleStartsAt` prop backstops it for
   * the competition page's root schedule before the filters resolve; null
   * falls through to `resolvePastWindowStart`'s 2-year floor.
   */
  const resolvedScheduleStartsAt =
    findScheduleStartsAt(filters, selectedScheduleId || schedule_id) ??
    scheduleStartsAt

  const [slug, setSlug] = useState(
    currentParams?.sn_division
      ? `/schedules/${currentParams.sn_division}/games`
      : currentParams?.sn_schedule
      ? `/schedules/${currentParams.sn_schedule}/games`
      : _slug
  )

  const { setParams } = useUrlManager()
  const setParamSort = (value) => setParams({ sn_sort: value })

  const {
    mode,
    calendarSubView,
    calendarDate,
    didFallBackFromDetails,
    isMobile,
    setMode,
    setCalendarSubView,
    setCalendarDate,
  } = useGameViewMode({ supportsDetails: showDetailsView })

  // `useGetScheduleFiltering` already loaded from `/teams/{id}/schedules/V2`
  // for the dropdowns. That response now carries `team_roster_id`, so no
  // separate `/teams/{id}/rosters` request is needed.
  //
  // Division id is passed as well as season: `team_roster_id` sits on the level
  // the team actually registered in (usually the division) and is null on the
  // parent season, so a season-only lookup would find nothing.
  const eventsRosterId = resolveRosterIdFromFilters(
    filters,
    currentParams?.sn_schedule,
    currentParams?.sn_division
  )

  // Hand it up so the Team page can gate its Add Event action on it. Fired on
  // change only; `onRosterIdChange` is excluded from the deps because parents
  // commonly pass an inline arrow, which would re-run this every render.
  useEffect(() => {
    onRosterIdChange?.(eventsRosterId ?? '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventsRosterId])

  // Events come from their own endpoint, and WHICH endpoint depends on what
  // this list is showing:
  //
  // - Team page (`team_id` prop) → that team's events. A selected
  //   season/division narrows to its roster; "All Seasons" takes the team-wide
  //   list.
  // - Competition Schedule page (`schedule_id`, no `team_id`) → the
  //   competition's events, scoped by the page's own filters.
  //
  // The team scoping reads the `team_id` PROP, never the `team` filter value:
  // on the competition page `team` holds the `sn_team` FILTER, and treating it
  // as the page's team would send a competition-wide list down the team path.
  // The competition hook takes that filter separately and scopes by schedule.
  const isTeamScoped = Boolean(team_id)
  const hasScheduleSelection = Boolean(
    currentParams?.sn_schedule || currentParams?.sn_division
  )

  const [timePeriod, setTimePeriod] = useState('TODAY')
  const [datesResolved, setDatesResolved] = useState(false)
  // `/games/dates` result. `null` means "not answered yet" — distinct from `[]`
  // ("answered: no games"), which is a valid input to the period decision.
  const [gameDates, setGameDates] = useState(null)
  // The opening period is decided once, when games AND events are both in.
  const [periodResolved, setPeriodResolved] = useState(false)
  // Set when the user picks a period from the toggle. See the re-decide below.
  const [periodPickedByUser, setPeriodPickedByUser] = useState(false)
  const hasCustomDateRange = !!(dateRange?.starts_at || dateRange?.ends_at)

  const getDateRangeForTimePeriod = (period) => {
    const now = dayjs()
    switch (period) {
      case 'TODAY':
        return {
          starts_at: now.startOf('day').format('YYYY-MM-DD'),
          ends_at: now.endOf('day').format('YYYY-MM-DD'),
        }
      case 'PAST':
        return {
          starts_at: resolvePastWindowStart(resolvedScheduleStartsAt).format(
            'YYYY-MM-DD'
          ),
          ends_at: now.subtract(1, 'day').endOf('day').format('YYYY-MM-DD'),
        }
      case 'UPCOMING':
      default:
        return {
          starts_at: now.startOf('day').format('YYYY-MM-DD'),
          ends_at: now.add(1, 'year').endOf('day').format('YYYY-MM-DD'),
        }
    }
  }

  // The calendar owns the window while it is on screen; otherwise the filters
  // do. Deriving it here rather than having the calendar report it up means the
  // query and the viewport can never be one render out of step.
  const periodRange = getDateRangeForTimePeriod(timePeriod)
  const filterRange = hasCustomDateRange
    ? toCivilRange(dateRange.starts_at, dateRange.ends_at)
    : toCivilRange(periodRange.starts_at, periodRange.ends_at)

  //  The calendar used to open on the current real-world month whatever the
  //  filters said — a team whose season ended in March landed on an empty
  //  August, with the period control disabled (every bounded layout drives its
  //  own window) and nothing to steer with but the month arrows. So when the
  //  user has NOT picked a month of their own, one is derived from the same
  //  filter the List and Details views read.
  const wantsDerivedAnchor =
    mode === 'calendar' &&
    calendarSubView !== 'agenda' &&
    calendarDate === null &&
    loaded &&
    datesResolved

  //  An explicit date range is already the answer to "where are the games I
  //  want", so it needs no day index — and it outranks the period, exactly as
  //  it does for the query window below.
  const { anchorDate: periodAnchorDate, isResolving: isResolvingAnchor } =
    useCalendarAnchor({
      enabled: wantsDerivedAnchor && !hasCustomDateRange,
      period: timePeriod,
      range: filterRange,
      scheduleId: selectedScheduleId || schedule_id,
      teamId: team_id,
    })

  const derivedAnchorDate = !wantsDerivedAnchor
    ? null
    : hasCustomDateRange
    ? calendarAnchorForRange(filterRange, todayCivilDate())
    : periodAnchorDate

  //  Null on both sides means "today", which `calendarViewportRange` already
  //  does — a period with no games has no better month to offer.
  const calendarAnchor = calendarDate ?? derivedAnchorDate

  const calendarRange =
    mode === 'calendar'
      ? calendarViewportRange(calendarAnchor, calendarSubView)
      : null

  //  `calendarViewportRange` returns null for exactly one layout: agenda, which
  //  has no viewport of its own and lists whatever the filters select. So this
  //  is true for the bounded grids (month/week/day) and false everywhere else.
  //
  //  It decides how the view pages. Bounded grids load their window to
  //  completion; everything that scrolls — List, Details and agenda — pulls
  //  pages in as the user scrolls. Agenda used to take the bounded path, which
  //  meant a season-long filter walked the whole range 200 games at a time
  //  before anything rendered.
  const hasBoundedWindow = mode === 'calendar' && calendarRange !== null

  //  Views that page by scrolling the document. Details also scrolls, but
  //  through its own AG Grid body via `onReachBottom`, not the window.
  const isDocumentScrollPaged =
    mode === 'list' || (mode === 'calendar' && !hasBoundedWindow)

  //  An explicitly chosen date range is the user's instruction and outranks the
  //  calendar's own viewport. Letting the viewport win meant picking a range,
  //  switching to Calendar, and silently getting the whole month back.
  //
  //  Details takes no period window at all — see `resolveGameListWindow`.
  const civilRange = resolveGameListWindow({
    mode,
    hasCustomDateRange,
    filterRange,
    calendarRange,
  })

  // The window the events ask for — THE SAME ONE THE GAMES ASK FOR.
  //
  // `civilRange` is the resolved answer: a bounded calendar viewport when one
  // is on screen, else an explicit date range, else the selected period. Taking
  // it from here rather than rebuilding it from the period is what makes the
  // calendar work — paging to another month changes `civilRange`, which changes
  // the events query key, which fetches that month's events. Before this, games
  // followed the viewport and events stayed pinned to the period, so a month
  // outside it rendered games only.
  //
  // `civilRange` is null only in Details, which renders no events at all; it
  // falls back to the filter's own range so that view is unaffected.
  const eventCivilRange = civilRange ?? filterRange

  // Civil dates, so the bounds are days rather than instants — and
  // `endExclusive` is the day AFTER the last one wanted (see `toCivilRange`),
  // while `starts_before` is inclusive. Stepping back a day before taking the
  // end of it is what keeps the two conventions from drifting by a day.
  const eventStartsAfter = eventCivilRange?.start
    ? dayjs(eventCivilRange.start).startOf('day').format()
    : null
  const eventStartsBefore = eventCivilRange?.endExclusive
    ? dayjs(eventCivilRange.endExclusive)
        .subtract(1, 'day')
        .endOf('day')
        .format()
    : null

  // The TEAM path's events. Now WINDOWED and SORTED server-side: these
  // endpoints always accepted the params, but a complete list could be ordered
  // after it arrived, so the fetch-all hook never sent them. A paged stream
  // must — each page has to continue where the last one stopped, or the merge's
  // boundary means nothing.
  const {
    events: teamEvents,
    frontier: teamEventsFrontier,
    isLoading: teamEventsLoading,
    isFetching: teamEventsFetching,
    fetchNextPage: fetchNextTeamEvents,
  } = useScheduleEventStream({
    teamId: team_id,
    rosterId: eventsRosterId,
    hasScheduleSelection,
    // `loaded` flips once the filters resolve; until then a selected season's
    // roster is simply unknown rather than absent.
    isResolvingRoster: !loaded,
    startsAfter: eventStartsAfter,
    startsBefore: eventStartsBefore,
    order: sort,
  })

  const {
    events: competitionEvents,
    frontier: competitionEventsFrontier,
    isLoading: competitionEventsLoading,
    isFetching: competitionEventsFetching,
    fetchNextPage: fetchNextCompetitionEvents,
  } = useCompetitionEventStream({
    // `selectedScheduleId` already collapses division → season → page id with
    // the same precedence, and it is what the games slug follows — so events
    // and games stay pointed at the same schedule once `onScheduleChange`
    // moves it. `schedule_id` backstops the first render, before it settles.
    scheduleId: selectedScheduleId || schedule_id,
    // The team FILTER, so events narrow in step with the games list, which is
    // built from the same value.
    teamId: team,
    venueId: venue,
    facilityId: facility,
    // The selected period's window, matching the games request — the same
    // arrangement My Schedule uses. Part of the query key, so switching period
    // refetches on its own rather than re-filtering a year of events.
    startsAfter: eventStartsAfter,
    startsBefore: eventStartsBefore,
    order: sort,
    // Idle on the Team page, and until the filters resolve — a selection read
    // mid-load would fetch the unfiltered competition and then refetch.
    enabled: !isTeamScoped && loaded,
  })

  // SEPARATE queries, feeding only the opening-tab decision.
  //
  // `decidePeriod` asks "does this schedule have anything today or later?" — a
  // question the windowed lists above cannot answer, since each holds ONE
  // period's events by construction. Asking Past would otherwise decide there
  // is no future activity and open on Past, stranding a schedule whose only
  // events are next month.
  //
  // ONE ROW settles it: the earliest event from today onward is either today
  // (TODAY), later (UPCOMING), or absent (PAST), and no further row can change
  // that. See `resolveDecisionEventQuery`.
  //
  // The TEAM path needs its own now. It used to reuse the team list, which
  // worked only because that list was unwindowed — windowing it for pagination
  // took that away, and without this the opening tab would be decided from
  // whichever period happened to be selected.
  const decisionQuery = resolveDecisionEventQuery()

  const competitionDecisionFilters = {
    ...buildCompetitionEventsQuery({
      teamId: team,
      venueId: venue,
      facilityId: facility,
    }),
    ...decisionQuery,
  }

  const {
    eventStartsAt: competitionDecisionStartsAt,
    isLoading: competitionEventsForDecisionLoading,
  } = useEventExistsFromToday({
    queryKey: queryKeys.events.decision(
      'schedule',
      selectedScheduleId || schedule_id || null,
      competitionDecisionFilters
    ),
    fetchPage: () =>
      listScheduleEventsPage(
        selectedScheduleId || schedule_id,
        competitionDecisionFilters,
        1
      ),
    enabled:
      !isTeamScoped && loaded && Boolean(selectedScheduleId || schedule_id),
  })

  // The team decision follows the same roster-vs-all-seasons split the list
  // does, so it asks the endpoint the list is actually reading from.
  const isTeamRosterScoped = isTeamScoped && hasScheduleSelection

  /*
   * A roster-scoped selection the team has no roster in is a SETTLED answer,
   * not a pending one.
   *
   * `eventsRosterId` is derived from `filters`, which `loaded` reports as
   * final -- so once loaded, a missing roster will never arrive. Leaving the
   * query merely disabled deadlocked the page: `useEventExistsFromToday`
   * reports a disabled query as `isLoading: true`, which held
   * `eventsForDecisionLoading`, which blocked `periodResolved`, which kept the
   * games list `enabled: false` -- and `useGameListItems` reports a gated
   * query as loading too. The skeleton then never cleared.
   *
   * Placeholder teams ("TBD North First") hit this on every visit: they are
   * not registered in a division, so no descendant of the selection carries a
   * `team_roster_id`.
   */
  const rosterMissingForSelection =
    isTeamRosterScoped && loaded && !eventsRosterId

  const {
    eventStartsAt: teamDecisionStartsAt,
    isLoading: teamEventsForDecisionLoading,
  } = useEventExistsFromToday({
    queryKey: queryKeys.events.decision(
      team_id ?? null,
      isTeamRosterScoped ? eventsRosterId ?? null : 'all-seasons',
      decisionQuery
    ),
    fetchPage: () =>
      isTeamRosterScoped
        ? listRosterEventsPage(team_id, eventsRosterId, decisionQuery, 1)
        : listTeamEventsPage(team_id, decisionQuery, 1),
    enabled:
      isTeamScoped &&
      loaded &&
      (isTeamRosterScoped ? Boolean(eventsRosterId) : true),
    // Nothing to wait for: report settled so the period can resolve and the
    // list can fall through to its empty state.
    settledWhenDisabled: rosterMissingForSelection,
  })

  const events = isTeamScoped ? teamEvents : competitionEvents
  // Already bounded to today-onward by the query, so no client-side narrowing
  // is needed — the rows that came back ARE the candidates.
  const eventsForDecisionStartsAt = isTeamScoped
    ? teamDecisionStartsAt
    : competitionDecisionStartsAt
  // The decision must wait on the query it actually READS. Gating on the
  // windowed list's loading state instead let the opening tab be decided from
  // an empty array while this query was still in flight — the tab then landed
  // on whichever query happened to settle first.
  const eventsForDecisionLoading = isTeamScoped
    ? teamEventsForDecisionLoading
    : competitionEventsForDecisionLoading
  const eventsLoading = isTeamScoped
    ? teamEventsLoading
    : competitionEventsLoading

  const handleTimePeriodChange = (period) => {
    // A deliberate pick — suppresses the empty-period re-decide below, so
    // selecting an empty period is not immediately undone.
    setPeriodPickedByUser(true)
    setTimePeriod(period)
    // No explicit event refetch here any more. Both event streams now send the
    // period's window, so it is part of their cache key and switching period
    // moves to a different entry that fetches on its own.
    //
    // Calling one WOULD be actively wrong: the refetcher is built during the
    // render that still holds the OUTGOING period, so it would refetch every
    // loaded page of the tab the user just left.
    //  The month in the URL was chosen for the PREVIOUS period. Keeping it
    //  would leave Upcoming opening on a month of finished games — picking a
    //  period is the newer instruction, so the calendar re-derives from it.
    setCalendarDate(null)
    if (period === 'PAST') {
      setSort('desc')
      setParamSort('desc')
    } else {
      setSort('asc')
      setParamSort('asc')
    }
  }

  useEffect(() => {
    if (hasCustomDateRange) {
      setDatesResolved(true)
      return
    }

    // Wait for the schedule filter to finish loading so we know which
    // season/division is selected before fetching dates.
    if (!loaded) {
      return
    }

    let cancelled = false
    const fetchDates = async () => {
      try {
        const today = dayjs()
        const startsAfter = today.subtract(6, 'month').format()
        const startsBefore = today.add(6, 'month').format()

        const query = {
          starts_after: startsAfter,
          starts_before: startsBefore,
        }

        // Scope the dates query to the selected schedule so the tab
        // selection matches the games that will actually be displayed.
        if (selectedScheduleId) {
          query.root_schedule_id = selectedScheduleId
        } else if (schedule_id) {
          query.root_schedule_id = schedule_id
        }

        if (team_id) {
          query.team_id = team_id
        }

        const { data } = await req('/games/dates', { query })

        if (cancelled) {
          return
        }

        const normalizedDates = (data || []).map((d) => {
          const raw = Array.isArray(d) ? d[0] : d
          return dayjs(raw).format('YYYY-MM-DD')
        })

        // Only record the dates — the period is decided in one place below,
        // once events are in too. Deciding here would ignore a team whose only
        // upcoming item is an event.
        setGameDates(normalizedDates)
      } catch {
        // Treat a failed dates call as "no games": events alone then decide,
        // and with neither the decision falls through to PAST as before.
        if (!cancelled) {
          setGameDates([])
        }
      } finally {
        if (!cancelled) {
          setDatesResolved(true)
        }
      }
    }

    fetchDates()
    return () => {
      cancelled = true
    }
  }, [hasCustomDateRange, loaded, selectedScheduleId, schedule_id, team_id])

  //  Only Details asks for a null range meaning "every date". Everywhere else a
  //  null range is the legacy unfiltered view, which still wants `default=1`.
  const wantsAllDates = mode === 'details' && !hasCustomDateRange

  //  Oldest/Newest orders a flat list; Calendar and Details impose their own
  //  ordering, so it would be a lie there. Details also drops the period
  //  control, and on the Schedule page the switcher lives on the sub-tab row —
  //  which together can leave this row with nothing at all to show.
  const showTimePeriodFilter = mode !== 'details'
  const showSortGroup = mode === 'list'
  const showInlineSwitcher = !areFiltersHidden && !hasHostViewSwitcher
  const hasFilterActionsRow =
    showTimePeriodFilter || showSortGroup || showInlineSwitcher

  const queries = buildGameListQueries({
    sort,
    teamId: team_id || team || null,
    venueId: venue || null,
    facilityId: facility || null,
    civilRange,
    hasCustomDateRange,
    hasExplicitSort: Boolean(currentParams?.sn_sort),
    wantsAllDates,
    // A bounded calendar window and Details both render a whole window at once,
    // so a big page is the right trade — the default size would turn one month
    // into a dozen sequential requests.
    //
    // Agenda is excluded: it pages by scrolling, and a 200-game page is a slow
    // request to sit behind before the first row appears. It takes the same
    // default page size the List view has always used.
    perPage: isDocumentScrollPaged ? undefined : 200,
  })

  //  Not while the opening month is still unknown: fetching today's month first
  //  and the real one a moment later is two requests and a visible jump.
  // `periodResolved` as well as `datesResolved`: the dates effect now only
  // RECORDS the dates — the period is decided once events have settled too, so
  // fetching on `datesResolved` alone would query the default period and then
  // refetch when the real one lands.
  //
  // A custom date range overrides the period entirely, so it does not wait.
  const enabled =
    loaded &&
    datesResolved &&
    (periodResolved || hasCustomDateRange) &&
    !isResolvingAnchor

  const {
    games,
    rawGames,
    isLoading,
    isError,
    error,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    loadAll,
    isLoadingAll,
    refetch,
    counts,
    queryKey,
    showEmptyState,
  } = useGameListItems({
    slug,
    queries,
    civilRange,
    enabled,
    refreshToken: Number(refreshKey) || 0,
    mode,
    hasBoundedWindow,
  })

  const { venuesById } = useVenueOptions(
    selectedScheduleId || schedule_id,
    venues,
    rawGames
  )

  // Export carries the user's ORIGINAL filter semantics, never the padding the
  // calendar needed. Written only when it actually changes, so appending a page
  // cannot retrigger the parent and reset its scroll.
  const exportSignature = JSON.stringify(queries.export) + selectedScheduleId
  const lastExportRef = useRef('')
  useEffect(() => {
    if (lastExportRef.current === exportSignature) {
      return
    }
    lastExportRef.current = exportSignature
    setFilterForExporting({
      ...queries.export,
      _exportScheduleId: selectedScheduleId,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exportSignature])

  const visibleIdsRef = useRef('')
  useEffect(() => {
    if (typeof setVisibleGameIds !== 'function') {
      return
    }
    const ids = games.map((game) => game.id)
    const signature = ids.join(',')
    if (visibleIdsRef.current === signature) {
      return
    }
    visibleIdsRef.current = signature
    setVisibleGameIds(ids)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [games])

  // Still filtered client-side even though BOTH paths now window server-side:
  // the server's bounds are inclusive of whole days either end. This is the
  // belt to that braces — it costs nothing on an already-narrow page and keeps
  // the two paths rendering identically. Only the date window and sort apply;
  // Division/Location/Surface have no event-side field to match, so events keep
  // showing while one of those is set.
  const filteredEvents = filterEvents(events, {
    startsAfter: eventStartsAfter,
    startsBefore: eventStartsBefore,
    order: sort,
  })

  // BOTH sides paginate now, so either can be the one that has not reached far
  // enough — the watermark holds back whichever rows sit beyond the nearer
  // frontier, GAMES included. Previously only events were ever withheld,
  // because they arrived complete and could not lag.
  const {
    visibleGames,
    visibleEvents,
    hasNextPage: hasMoreRows,
    fetchNextPage: fetchNextRows,
  } = useScheduleStream({
    games,
    gamesFrontier: {
      lastStartsAt: frontierOf(games, sort),
      hasMore: hasNextPage,
      // While games are loading, `games` holds either nothing or the PREVIOUS
      // period's rows — neither is something the watermark can reason against.
      isPending: isLoading,
    },
    events: filteredEvents,
    eventsFrontier: isTeamScoped
      ? teamEventsFrontier
      : competitionEventsFrontier,
    order: sort,
    fetchNextGames: fetchNextPage,
    fetchNextEvents: isTeamScoped
      ? fetchNextTeamEvents
      : fetchNextCompetitionEvents,
    isFetching:
      isFetchingNextPage ||
      (isTeamScoped ? teamEventsFetching : competitionEventsFetching),
    // A bounded calendar window draws a month, week or day all at once and has
    // nothing to scroll, so it loads to completion instead of watermarking —
    // the same call the games query already makes via `CALENDAR_PAGE_CAP`.
    bypass: hasBoundedWindow,
    // Filters, period and sort all live in the games query key; changing any of
    // them should let the stall guard try again.
    resetKey: JSON.stringify(queryKey),
  })

  // Rebuilt every render, while the scroll listener is attached once. A ref
  // keeps the listener stable and still calls the current one.
  const fetchNextRowsRef = useRef(fetchNextRows)
  fetchNextRowsRef.current = fetchNextRows

  // Scroll-driven pagination belongs to the views that scroll the document:
  // List, and the calendar's agenda layout. The bounded grids page themselves
  // through the auto-follow above, and Details drives its own AG Grid body
  // scroll via `onReachBottom`.
  //
  // `fetchNextRows`, not `fetchNextPage`: it advances whichever STREAM is
  // holding the merge back. Advancing games unconditionally would pull a page
  // that changes nothing visible whenever events are the ones lagging, leaving
  // the list still while requests went out.
  useEffect(() => {
    if (!isDocumentScrollPaged) {
      return undefined
    }
    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = document.documentElement
      const nearBottom =
        scrollHeight - (scrollTop + clientHeight) < SCROLL_THRESHOLD
      if (nearBottom) {
        fetchNextRowsRef.current()
      }
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [isDocumentScrollPaged])

  useEffect(() => {
    if (
      !loaded ||
      !datesResolved ||
      currentParams?.sn_sort ||
      hasCustomDateRange ||
      timePeriod !== 'PAST' ||
      sort === 'desc'
    ) {
      return
    }
    setSort('desc')
    setParamSort('desc')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentParams?.sn_sort,
    datesResolved,
    hasCustomDateRange,
    loaded,
    sort,
    timePeriod,
  ])

  // Shared by the initial decision and the empty-period re-decide below, so
  // both apply the same window and the same games-first rule.
  const decidePeriod = useCallback(() => {
    // Narrowed to `/games/dates`' own ±6 month window so both sides of the
    // decision answer the same question.
    const eventStartsAt = eventsForDecisionStartsAt

    // The shared helper returns lowercase tab keys; this list uses uppercase.
    const decided = resolveDefaultScheduleTab({
      gameDates,
      eventStartsAt,
    }).toUpperCase()

    // `setTimePeriod` is a no-op when the value is unchanged, so re-deciding
    // to the same period cannot loop.
    setTimePeriod(decided)
    if (decided === 'PAST') {
      setSort('desc')
      setParamSort('desc')
    }
    // `setParamSort` is recreated every render (a plain arrow over `setParams`),
    // so listing it would make this callback unstable and re-run the effect
    // below on every render. The state setters it calls are stable by React's
    // own guarantee.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventsForDecisionStartsAt, gameDates])

  // The one place the opening period is decided. Both inputs must be in
  // first: `/games/dates` has answered, and the events query has settled.
  useEffect(() => {
    if (periodResolved || gameDates === null || eventsForDecisionLoading) {
      return
    }
    decidePeriod()
    setPeriodResolved(true)
  }, [periodResolved, gameDates, eventsForDecisionLoading, decidePeriod])

  // Jump to the period holding a just-saved event. Treated as a deliberate
  // selection, because it follows from an action the user took — so the
  // empty-period re-decide will not immediately second-guess it.
  const followEvent = useCallback(
    (saved) => {
      const target = resolveTabForDate(saved?.starts_at)
      if (!target) {
        return
      }
      const period = target.toUpperCase()
      setPeriodPickedByUser(true)
      setPeriodResolved(true)
      setTimePeriod(period)
      setCalendarDate(null)
      if (period === 'PAST') {
        setSort('desc')
        setParamSort('desc')
      } else {
        setSort('asc')
        setParamSort('asc')
      }
      // Same reason as `decidePeriod` above: `setParamSort` is a new function
      // each render, and the rest are stable state setters.
    },
    [setSort]
  )

  // `onFollowEventReady` is excluded from the deps on purpose: callers pass an
  // inline arrow, which would make this re-run every render. Same rationale as
  // `onRosterIdChange` above.
  useEffect(() => {
    onFollowEventReady?.(followEvent)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [followEvent])

  // Folded into the day buckets rather than handed to DayOfGames as a mixed
  // list: that component is shared with MySchedule and OrgSchedule, neither of
  // which has events, so events stay inside this feature.
  const dayBuckets = mergeEventsIntoDayBuckets(
    groupGamesByDay(visibleGames),
    visibleEvents,
    sort
  )

  // Games AND events on the current period — what "is this period empty?"
  // actually means once events are in the list.
  const rowCount = dayBuckets.reduce(
    (total, bucket) => total + bucket.items.length,
    0
  )

  // Whether the CURRENT period has ever shown rows since it was selected.
  // Reset on every period change, so landing on an already-empty period leaves
  // it false while a period that later empties out leaves it true — the
  // difference between "the user chose this empty view" and "a mutation took
  // its contents away".
  const hadItemsSinceSelection = useRef(false)
  // The period `hadItemsSinceSelection` was last observed for. Switching
  // periods does not immediately refetch, so for one commit the row count
  // still holds the PREVIOUS period's rows while `timePeriod` is already the
  // new one. Recording the period alongside the observation makes that stale
  // window detectable instead of letting it mark the new period as "had
  // items" — which is what made Past -> Today jump to Upcoming.
  const itemsObservedForPeriod = useRef(null)

  useEffect(() => {
    hadItemsSinceSelection.current = false
    itemsObservedForPeriod.current = null
  }, [timePeriod])

  // Creating or deleting an event can empty the period the user is on. React
  // Query has already refreshed by then, so re-run the same decision and move
  // to a period that actually has something. Only when the current period is
  // EMPTY, and never when the user chose it while it still had content.
  useEffect(() => {
    if (hasCustomDateRange) {
      return
    }
    const settled = !isLoading && !eventsLoading
    // Only trust a count already observed for THIS period: the first settled
    // reading after a change can still be the previous period's data.
    const countIsForCurrentPeriod =
      itemsObservedForPeriod.current === timePeriod
    if (settled) {
      if (countIsForCurrentPeriod && rowCount > 0) {
        hadItemsSinceSelection.current = true
      }
      itemsObservedForPeriod.current = timePeriod
    }

    if (
      !countIsForCurrentPeriod ||
      !shouldRedecideScheduleTab({
        resolved: periodResolved,
        inputsSettled: settled,
        currentTabItemCount: rowCount,
        tabPickedByUser: periodPickedByUser,
        emptiedSinceSelection: hadItemsSinceSelection.current,
      })
    ) {
      return
    }
    decidePeriod()
    // The two refs above are deliberately not deps: they are read, not
    // reacted to — listing them would do nothing, since ref mutations do not
    // re-render.
  }, [
    hasCustomDateRange,
    periodResolved,
    periodPickedByUser,
    timePeriod,
    isLoading,
    eventsLoading,
    rowCount,
    decidePeriod,
  ])

  // `showEmptyState` comes from the GAMES query, which knows nothing about
  // events — so a period holding only events reported "no games" and hid the
  // list, taking the events with it. The empty state now needs both halves to
  // be empty. `eventsLoading` is included so the message does not flash while
  // events are still in flight.
  const hasAnyRow = rowCount > 0
  // `showEmptyState` comes from the GAMES query alone. With both sides paging,
  // "nothing here" also requires the EVENT stream to be exhausted — otherwise a
  // period whose games are done but whose events are still arriving reports
  // empty and hides the rows about to land.
  const showEmptyStateWithEvents =
    showEmptyState && !eventsLoading && !hasMoreRows && !hasAnyRow

  return (
    <div
      css={css`
        margin-top: 24px;
        margin-bottom: 84px;
      `}
    >
      {/*
        `GameListFilters` returns null when `isHidden` is set, which would take
        the switcher with it — so when the filters are hidden it gets its own
        row instead of disappearing.
      */}
      {areFiltersHidden && !hasHostViewSwitcher && (
        <div css={switcherRowStyles}>
          <ViewSwitcher
            mode={mode}
            onChange={setMode}
            disabled={isLoading}
            showDetails={showDetailsView && !isMobile}
          />
        </div>
      )}

      {didFallBackFromDetails && (
        <p css={fallbackNoticeStyles} role='status'>
          {gameViewsLabel('gameViewDetailsMobileFallback')}
        </p>
      )}

      <GameListFilters
        filters={filters}
        dateRange={dateRange}
        team_id={team_id}
        onTeamChange={setTeam}
        onScheduleChange={(newScheduleId) => {
          setDatesResolved(false)
          if (newScheduleId) {
            setSelectedScheduleId(newScheduleId)
            setSlug(`/schedules/${newScheduleId}/games`)
          } else {
            setSelectedScheduleId('')
            setSlug(_slug)
          }
        }}
        setDateRange={setDateRange}
        hasCustomDateRange={hasCustomDateRange}
        state={state}
        setState={setState}
        sort={sort}
        setSort={setSort}
        teams={teams}
        team={team}
        loading={isLoading}
        useDateRange
        isHidden={areFiltersHidden}
        venues={venues}
        venue={venue}
        onVenueChange={setVenue}
        facility={facility}
        onFacilityChange={setFacility}
        loadingVenues={loadingVenues}
        useVenueFilter
      >
        {hasFilterActionsRow && (
          <div css={filterActionsRowStyles}>
            {/*
              Not in Details. Past ends yesterday and Upcoming starts today, so
              the tabs cut a running season in two and no selection can reach
              across the cut — which is the one thing that grid is for. It works
              over every date instead, and Date Range narrows it precisely.
            */}
            {showTimePeriodFilter && (
              <TimePeriodFilter
                selected={timePeriod}
                onSelect={handleTimePeriodChange}
                //  Agenda has no viewport of its own — it lists whatever the
                //  filters select — so the period control is live there. Month,
                //  week and day are driven by their own navigation instead.
                disabled={
                  isLoading ||
                  hasCustomDateRange ||
                  (mode === 'calendar' && calendarSubView !== 'agenda')
                }
              />
            )}
            {/*
              The switcher shares this row's right edge with the sort control
              rather than taking a row of its own — a lone control on an
              otherwise empty line reads as a layout mistake, and the filter
              grid already establishes where the right edge is.
            */}
            <div css={trailingControlsStyles}>
              {showSortGroup && (
                <div css={sortGroupStyles(isLoading)}>
                  <button
                    type='button'
                    disabled={isLoading}
                    onClick={() => {
                      setSort('asc')
                      setParamSort('asc')
                    }}
                    css={sortButtonStyles(sort === 'asc', isLoading, 'left')}
                  >
                    {gameViewsLabel('gameSortOldest')}
                  </button>
                  <button
                    type='button'
                    disabled={isLoading}
                    onClick={() => {
                      setSort('desc')
                      setParamSort('desc')
                    }}
                    css={sortButtonStyles(sort === 'desc', isLoading, 'right')}
                  >
                    {gameViewsLabel('gameSortNewest')}
                  </button>
                </div>
              )}
              {showInlineSwitcher && (
                <ViewSwitcher
                  mode={mode}
                  onChange={setMode}
                  disabled={isLoading}
                  showDetails={showDetailsView && !isMobile}
                />
              )}
            </div>
          </div>
        )}
      </GameListFilters>

      {mode === 'details' ? (
        <DetailsPanel
          games={games}
          rawGames={rawGames}
          venuesById={venuesById}
          gameTypes={gameTypes || []}
          officialTypes={officialTypes || []}
          organizationId={organizationId}
          //  `scheduleId` is the team-page fallback: that host has no
          //  `schedule_id` of its own but scopes the list to one competition.
          scheduleId={schedule_id || scheduleId}
          civilRange={civilRange}
          canEdit={canUpdate}
          loading={isLoading}
          counts={counts}
          isFetchingNextPage={isFetchingNextPage}
          onReachBottom={fetchNextPage}
          isError={isError}
          errorMessage={error?.message}
          onRetry={refetch}
          isLoadingAll={isLoadingAll}
          onLoadAll={loadAll}
          queryKey={queryKey}
          organizationName={organizationName}
          sportId={sportId}
          //  The edit modal's mutations invalidate with `refetchType: 'none'`,
          //  which is right for views nobody is looking at. This grid IS on
          //  screen, so it refetches explicitly to show the change in place.
          onGameChanged={refetch}
          canDeleteGames={canDeleteGames}
        />
      ) : mode === 'calendar' ? (
        <CalendarPanel
          //  The SAME sets the list view folds into its day buckets, so
          //  switching views can never change which rows are on screen. A
          //  bounded calendar window bypasses the watermark, so month/week/day
          //  get everything loaded; agenda scrolls and pages like List does.
          games={visibleGames}
          events={visibleEvents}
          venuesById={venuesById}
          subView={calendarSubView}
          onSubViewChange={setCalendarSubView}
          anchorDate={calendarAnchor}
          onAnchorDateChange={setCalendarDate}
          isSoccer={isSoccer}
          isHockey={isHockey}
          loading={isLoading}
          gameStatusTypes={gameStatusTypes || []}
          gameTypes={gameTypes || []}
          showSubViewTabs={!isMobile}
          //  With no single season chosen, one cell can hold a regular-season
          //  game and a playoff game side by side, and nothing on the card
          //  says which is which.
          showSeasonName={!selectedScheduleId}
          counts={counts}
          isError={isError}
          errorMessage={error?.message}
          onRetry={refetch}
          isLoadingAll={isLoadingAll}
          onLoadAll={loadAll}
        />
      ) : /* BOTH streams, not just games. The watermark admits nothing until
             each side has loaded the period being asked about, so releasing
             the skeleton on games alone showed an empty list for as long as
             the events request took. `eventsLoading` is true only with no data
             yet, so a background refetch will not re-show this. */
      isLoading || eventsLoading ? (
        <ScheduleCardSkeleton />
      ) : isError ? (
        <ListStatusView error={error?.message} />
      ) : showEmptyStateWithEvents ? (
        <EmptyState
          icon={<CalendarOutlined />}
          title='Nothing scheduled yet'
          subtitle="Games and events will appear here once they're added."
        />
      ) : (
        <>
          <Mobile>
            {(mobile) =>
              dayBuckets.map((bucket) => (
                <DayOfGames
                  key={bucket.dayKey}
                  date={formatCivilDay(bucket.dayKey, 'cccc, LLL d, yyyy')}
                  // Games only — `DayOfGames` is shared with MySchedule and
                  // OrgSchedule and knows nothing about events. Event rows go
                  // through its `extraRows` slot instead, so they sit inside
                  // the same collapsible day section.
                  games={bucket.items
                    .filter((row) => row.kind === 'game')
                    .map((row) => row.game)}
                  extraRows={bucket.items
                    .filter((row) => row.kind === 'event')
                    .map((row, idx, rows) => (
                      <EventListRow
                        key={`event-${row.event.id}`}
                        event={row.event}
                        hasNextSibling={idx !== rows.length - 1}
                      />
                    ))}
                  gameStatusTypes={gameStatusTypes}
                  gameTypes={gameTypes}
                  isMobile={mobile}
                  isSoccer={isSoccer}
                  isHockey={isHockey}
                  onClickFirstGame={onClickFirstGame}
                  scheduleId={schedule_id || scheduleId}
                />
              ))
            }
          </Mobile>
          <LoadMoreFooter
            counts={counts}
            isFetchingNextPage={isFetchingNextPage}
            isLoading={isLoading}
            visibleCount={games.length}
          />
        </>
      )}
      <FloatButton.BackTop
        icon={<ArrowUpOutlined />}
        css={css`
          .ant-float-btn-body {
            background-color: ${colors.PRIMARY} !important;
          }
          .ant-float-btn-icon {
            color: ${PRIMARY_BUTTON_CONTENT.icon} !important;
          }
        `}
      />
    </div>
  )
}

// ─── Styles ──────────────────────────────────────────────────────

const switcherRowStyles = css`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 16px;

  ${media.mobile} {
    justify-content: stretch;
  }
`

const filterActionsRowStyles = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  gap: 16px;

  ${media.mobile} {
    flex-direction: column;
    align-items: stretch;
  }
`

const trailingControlsStyles = css`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 0 0 auto;
  /* Keeps the switcher on the right edge when it is the row's only child:
     Details drops the period control, and space-between alone would pull it
     back to the left. */
  margin-left: auto;

  ${media.mobile} {
    justify-content: space-between;
    flex-wrap: wrap;
  }
`

const fallbackNoticeStyles = css`
  margin: 0 0 12px;
  font-size: 13px;
  color: ${colors.NEUTRAL_50};
`

const sortGroupStyles = (loading) => css`
  display: flex;
  align-items: center;
  gap: 0px;
  border: 1px solid ${colors.SECONDARY_200};
  border-radius: 8px;
  height: 40px;
  min-width: 180px;
  opacity: ${loading ? 0.5 : 1};
  transition: opacity 0.2s ease;
  ${media.mobile} {
    flex: 1;
  }
`

const sortButtonStyles = (isActive, loading, side) => css`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  height: 40px;
  border-radius: ${side === 'left' ? '8px 0 0 8px' : '0 8px 8px 0'};
  background-color: ${isActive ? colors.SECONDARY_300 : 'transparent'};
  border: none;
  cursor: ${loading ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  font-size: 14px;
  font-weight: ${isActive ? 700 : 400};
  line-height: 20.3px;
  color: ${colors.NEUTRAL_0};

  ${!loading &&
  css`
    &:hover {
      background-color: ${isActive
        ? colors.SECONDARY_300
        : colors.SECONDARY_200};
    }
    &:active {
      transform: scale(0.98);
    }
  `}
`

const mapStateToProps = (state, { sportId }) => {
  return {
    //  Scoped by sport, matching the status types below. `getAllGameTypes`
    //  flat-maps across every loaded sport, and the API hands each sport the
    //  same global `game_type` table — so it returned one duplicate set per
    //  sport, which the Details grid's type picker rendered verbatim.
    gameTypes: getGameTypesBySportId(state, sportId),
    gameStatusTypes: getGameStatusTypesBySportId(state, sportId),
    /*
     * NOT scoped by sport, unlike the two above. Every `official_type` row in
     * the database is hockey's, yet soccer, ball hockey and generic games all
     * carry officials — scoping this would empty the role picker for every
     * non-hockey competition. `getOfficialTypesBySportId` is deprecated for
     * exactly this reason.
     */
    officialTypes: getAllOfficialTypes(state),
  }
}

export default connect(mapStateToProps)(GameList)
